// ---------------------------------------------------------------------------
// Sidebar navigation config (AL-1850)
// ---------------------------------------------------------------------------
// Moved out of V11Layout so the order, the gating rules and the active-route
// rule can be tested without mounting the shell.
//
// Order follows CT-0: Dashboard, Channels, Feed, Discovery, Funnel, Compare,
// Overlap, Reports. Reports (AL-1860) reads GET /api/v11/reports.
//
// Every item is gated by a BE setting (AL-1896 added show_channels and
// show_reports, the last two that were always on).
// ---------------------------------------------------------------------------

import { Search, GitCompare, Layers, Radio, Filter, Rss, LayoutDashboard, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  readonly path: string;
  /** i18n key, resolved with t() at render time. */
  readonly label: string;
  /** i18n key for the second line the mobile nav still shows. */
  readonly description: string;
  readonly icon: LucideIcon;
  /**
   * A BE setting (config_definitions.key). When the account resolves it to
   * `false` the item is dropped. Items without one are always shown.
   */
  readonly feature?: string;
  /** A build-time flag, for items hidden per environment. */
  readonly envFlag?: string;
  /** Marks the item with the BETA chip. */
  readonly beta?: boolean;
}

export const FELTON_NAV_ITEMS: readonly NavItem[] = [
  {
    path: '/v11/dashboard',
    label: 'app.v11.nav.dashboard.label',
    description: 'app.v11.nav.dashboard.description',
    icon: LayoutDashboard,
    feature: 'show_dashboard',
    // Hidden in PRD for now (VITE_SHOW_DASHBOARD=false); visible in STG/DEV.
    envFlag: 'VITE_SHOW_DASHBOARD',
  },
  {
    path: '/v11/channels',
    label: 'app.v11.nav.channels.label',
    description: 'app.v11.nav.channels.description',
    icon: Radio,
    feature: 'show_channels',
  },
  {
    path: '/v11/feed',
    label: 'app.v11.nav.feed.label',
    description: 'app.v11.nav.feed.description',
    icon: Rss,
    feature: 'show_feed',
    beta: true,
  },
  {
    path: '/v11/discovery',
    label: 'app.v11.nav.discovery.label',
    description: 'app.v11.nav.discovery.description',
    icon: Search,
    feature: 'show_discovery',
  },
  {
    path: '/v11/funnel',
    label: 'app.v11.nav.funnel.label',
    description: 'app.v11.nav.funnel.description',
    icon: Filter,
    feature: 'show_funnel',
  },
  {
    path: '/v11/compare',
    label: 'app.v11.nav.compare.label',
    description: 'app.v11.nav.compare.description',
    icon: GitCompare,
    feature: 'show_compare',
  },
  {
    path: '/v11/overlap',
    label: 'app.v11.nav.overlap.label',
    description: 'app.v11.nav.overlap.description',
    icon: Layers,
    feature: 'show_overlap',
  },
  {
    path: '/v11/reports',
    label: 'app.v11.nav.reports.label',
    description: 'app.v11.nav.reports.description',
    icon: FileText,
    feature: 'show_reports',
  },
];

/**
 * An item stays active on its own path and on routes derived from it — Feed
 * keeps the highlight while the reader is on the Audience Lens
 * (/v11/feed/lens/:postId) or on the Wrapped Story (/v11/story/:postId), both
 * of which are downstream of Feed.
 */
export function isNavItemActive(pathname: string, navPath: string): boolean {
  if (pathname === navPath) return true;
  if (pathname.startsWith(`${navPath}/`)) return true;
  if (navPath === '/v11/feed' && pathname.startsWith('/v11/story/')) return true;
  return false;
}

type Settings = Readonly<Record<string, boolean | number | string | null>> | undefined;
type Env = Readonly<Record<string, unknown>>;

/**
 * Applies the two gates. A gated item is only rendered once `/me` has
 * resolved AND the setting is not `false`: showing it during the load and
 * taking it away afterwards reads as a glitch.
 */
export function visibleNavItems(
  items: readonly NavItem[],
  settings: Settings,
  env: Env
): readonly NavItem[] {
  return items.filter(item => {
    if (item.envFlag && env[item.envFlag] !== 'true') return false;
    if (!item.feature) return true;
    if (!settings) return false; // still loading — show nothing gated
    return settings[item.feature] !== false;
  });
}
